import { ParamsForMethod, useTellescopeMutation, useUsersMap } from "@tellescope/tanstack-query";
import { LabeledField, StateCredentialInfo } from "@tellescope/types-models";
import {
  Button,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PageTabs,
  PageTabsList,
  PageTabsTrigger,
} from "@tellescope/ui";
import { diffModelUpdate } from "@tellescope/utils";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AccessTabContent } from "./AccessTabContent";
import { ActivityTabContent } from "./ActivityTabContent";
import { AdminTabContent } from "./AdminTabContent";
import { PhoneTabContent } from "./PhoneTabContent";
import { ProfileTabContent } from "./ProfileTabContent/ProfileTabContent";
import { DEFAULT_NON_ADMIN_ROLE } from "./RolesSubPage/roleEditorConfig";
import { RoleTabContent } from "./RoleTabContent";
import { useEditUserForm } from "./useEditUserForm";

type UserUpdate = Omit<ParamsForMethod<"users", "updateOne">, "id" | "_options">;

export interface EditUserProfileDialogContentsProps {
  userId?: string;
  onSuccess?: () => void;
  onError?: () => void;
}

export const EditUserProfileDialogContents = ({ userId, onSuccess, onError }: EditUserProfileDialogContentsProps) => {
  const { t } = useTranslation("settings");
  const [selectedSection, setSelectedSection] = useState("profile");

  const userMap = useUsersMap();
  const user = userId ? userMap[userId] : undefined;

  const [newCredentialedStates, setNewCredentialedStates] = useState(user?.credentialedStates);
  const resolvedCredentialedStates = useMemo(
    () => newCredentialedStates ?? user?.credentialedStates ?? [],
    [newCredentialedStates, user?.credentialedStates]
  );
  const updateCredentialedState = useCallback(
    <T extends keyof StateCredentialInfo>(stateOrIndex: string | number, key: T, value: StateCredentialInfo[T]) => {
      setNewCredentialedStates((prev) => {
        const resolvedPrev = prev ?? resolvedCredentialedStates;
        const originalEntry =
          typeof stateOrIndex === "number"
            ? resolvedPrev?.[stateOrIndex]
            : resolvedPrev?.find(({ state: s }) => s === stateOrIndex);
        if (!originalEntry) return prev;
        const newEntry = {
          ...originalEntry,
          [key]: value,
        };
        return resolvedPrev.map((item, i) =>
          typeof stateOrIndex === "number"
            ? i === stateOrIndex
              ? newEntry
              : item
            : item.state === stateOrIndex
            ? newEntry
            : item
        );
      });
    },
    [resolvedCredentialedStates]
  );
  const addCredentialedState = useCallback(() => {
    setNewCredentialedStates((prev) => [...(prev ?? []), { state: "" }]);
  }, []);
  const deleteCredentialedState = useCallback((s: string | number) => {
    setNewCredentialedStates((prev) => prev?.filter(({ state }, i) => (typeof s === "number" ? i !== s : s !== state)));
  }, []);

  const [newTemplateFields, setNewTemplateFields] = useState(user?.templateFields);
  const resolvedTemplateFields = useMemo(
    () => newTemplateFields ?? user?.templateFields ?? [],
    [newTemplateFields, user?.templateFields]
  );
  const addTemplateField = useCallback(() => {
    setNewTemplateFields((prev) => [...(prev ?? []), { field: "", value: "" }]);
  }, []);
  const removeTemplateField = useCallback((i: number) => {
    setNewTemplateFields((prev) => {
      const arr = prev ?? [];
      return [...arr.slice(0, i), ...arr.slice(i + 1)];
    });
  }, []);
  const updateTemplateField = useCallback(<T extends keyof LabeledField>(i: number, key: T, value: LabeledField[T]) => {
    setNewTemplateFields((prev) => {
      const existingField = prev?.[i];
      if (!existingField) return prev;
      return [...prev.slice(0, i), { ...existingField, [key]: value }, ...prev.slice(i + 1)];
    });
  }, []);

  const form = useEditUserForm(user);

  // Normalized full snapshot of the desired user values: filter invalid array entries and
  // map the single-select role back to the array shape the model expects.
  const resolvedValues = useMemo<UserUpdate>(() => {
    const { values } = form;
    return {
      email: values.email,
      fname: values.fname,
      lname: values.lname,
      internalDisplayName: values.internalDisplayName,
      displayName: values.displayName,
      defaultLocationId: values.defaultLocationId,
      hideFromCalendarView: values.hideFromCalendarView,
      canvasId: values.canvasId,
      careTeamRole: values.careTeamRole,
      phone: values.phone,
      url: values.url,
      bio: values.bio,
      emailSignature: values.emailSignature,
      NPI: values.NPI,
      DEA: values.DEA,
      TIN: values.TIN,
      disableTicketAutoAssignment: values.disableTicketAutoAssignment,
      ticketAssignmentPriority: values.ticketAssignmentPriority,
      accountType: values.accountType,
      lockedOutUntil: values.lockedOutUntil,
      // Non-Admin is the empty-role baseline, so it maps to no roles rather than a stored role string.
      roles: values.roles && values.roles !== DEFAULT_NON_ADMIN_ROLE.role ? [values.roles] : [],
      suffixes: values.suffixes.filter(Boolean),
      requireSSO: values.requireSSO.filter(Boolean),
      availableFromNumbers: values.availableFromNumbers.filter(Boolean),
      credentialedStates: resolvedCredentialedStates.filter(({ state }) => state),
      templateFields: resolvedTemplateFields.filter(({ field, value }) => field && value),
    };
  }, [form, resolvedCredentialedStates, resolvedTemplateFields]);

  const diff = useMemo(() => diffModelUpdate<UserUpdate>(user, resolvedValues), [user, resolvedValues]);

  const updateUserMutation = useTellescopeMutation({
    model: "users",
    method: "updateOne",
    onSuccess: () => {
      toast.success(t("User updated successfully"));
      onSuccess?.();
    },
    onError: () => {
      toast.error(t("Failed to update user. Please try again."));
      onError?.();
    },
  });

  const handleSave = useCallback(() => {
    if (!userId || !diff.hasChanges) return;

    updateUserMutation.mutateAsync({
      id: userId,
      ...diff.updates,
      _options: {
        replaceObjectFields: diff.replaceObjectFields,
      },
    });
  }, [userId, diff, updateUserMutation]);

  return (
    <DialogContent divided style={{ maxWidth: "min(90vw, 700px)" }} className="w-[700px]">
      <PageTabs value={selectedSection} onValueChange={setSelectedSection}>
        <DialogHeader className="p-0 space-y-2">
          <DialogTitle className="px-6 pt-4">{t("Edit User Profile")}</DialogTitle>
          <PageTabsList className="h-10 px-6">
            <PageTabsTrigger value="profile">{t("Profile")}</PageTabsTrigger>
            <PageTabsTrigger value="admin">{t("Admin")}</PageTabsTrigger>
            <PageTabsTrigger value="role">{t("Role")}</PageTabsTrigger>
            <PageTabsTrigger value="phone">{t("Phone")}</PageTabsTrigger>
            <PageTabsTrigger value="activity">{t("Activity")}</PageTabsTrigger>
            <PageTabsTrigger value="access">{t("Access")}</PageTabsTrigger>
          </PageTabsList>
        </DialogHeader>
        <ProfileTabContent
          userId={userId}
          form={form}
          credentialedStates={resolvedCredentialedStates}
          onAddCredentialedState={addCredentialedState}
          onUpdateCredentialedState={updateCredentialedState}
          onDeleteCredentialedState={deleteCredentialedState}
          templateFields={resolvedTemplateFields}
          onAddTemplateField={addTemplateField}
          onUpdateTemplateField={updateTemplateField}
          onRemoveTemplateField={removeTemplateField}
        />
        <AdminTabContent form={form} />
        <RoleTabContent form={form} />
        <PhoneTabContent form={form} />
        <ActivityTabContent userId={userId} />
        <AccessTabContent form={form} />
      </PageTabs>
      <DialogFooter className="bg-gray-100 rounded-b-md flex justify-end items-center gap-2">
        <DialogClose>
          <Button variant="outline">{t("Cancel")}</Button>
        </DialogClose>
        <Button
          onClick={handleSave}
          disabled={updateUserMutation.isPending || (userId ? !diff.hasChanges || !form.isValid : !form.isValid)}
        >
          {userId ? t("Save Changes") : t("Create User")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
